import './activity_page.less';

import * as Campaigns       from '../campaigns';
import { parseISO }         from 'date-fns';
import { useEffect }        from 'react';
import { usePageContext }   from '../components/render_page';
import { useState }         from 'react';
import CalendarTime         from '../components/calendar_time';
import classnames           from 'classnames';
import ConfirmModal         from '../components/confirm_modal';
import ms                   from 'ms';
import Navbar               from '../components/navbar';
import pluralize            from '../util/pluralize';
import React                from 'react';
import useBoolean           from '../hooks/use_boolean';
import WaitFor              from '../components/wait_for';

import { ChevronRight }     from 'react-feather';
import { User as UserIcon } from 'react-feather';


export default function ActivityPage({ businessID }) {
  const statuses     = [ Campaigns.useCampaignsStatuses(businessID) ];
  const campaigns    = Campaigns.useCampaigns(businessID);
  const hasCampaigns = campaigns?.length > 0;

  return (
    <>
      <Navbar title="Activity"/>
      <div className="page-content">
        <WaitFor statuses={statuses}>
          {hasCampaigns && <CampaignSections campaigns={campaigns}/>}
          {!hasCampaigns && <EmptyView/>}
        </WaitFor>
      </div>
    </>
  );
}


function CampaignSections({ campaigns }) {
  const draftCampaigns     = campaigns?.filter(isDraft);
  const scheduledCampaigns = campaigns?.filter(isScheduled);
  const sentCampaigns      = campaigns?.filter(isSent);

  return (
    <>
      <Section
        title="Drafts"
        className="drafts"
        collection={draftCampaigns}
        component={DraftCampaign}
      />
      <Section
        title="Scheduled"
        className="scheduled"
        collection={scheduledCampaigns}
        component={ScheduledCampaign}
      />
      <Section
        title="Sent Campaigns"
        className="sent"
        collection={sentCampaigns}
        component={SentCampaign}
      />
    </>
  );
}


function Section({ title, className, collection, component: Campaign }) {
  if (collection?.length) {
    const classNames = classnames('section', className);
    return (
      <div className={classNames}>
        <h2 className="section-title">{title}</h2>
        <div className="items">
          {collection.map(campaign => (
            <Campaign key={campaign.id} {...{ campaign }}/>
          ))}
        </div>
      </div>
    );
  } else
    return null;
}


function DraftCampaign({ campaign }) {
  const { businessID } = usePageContext();
  const { navigateTo } = usePageContext();
  const deleteCampaign = Campaigns.useDeleteCampaign(businessID);
  const href           = `/${businessID}/campaigns/edit/${campaign.id}`;

  function onClickCampaign() {
    navigateTo(href);
  }

  const [ discardModalIsOpen, showDiscardModal, hideDiscardModal ] = useBoolean();

  function onClickDiscard(event) {
    event.stopPropagation();
    showDiscardModal();
  }

  async function onConfirmDelete() {
    hideDiscardModal();
    await deleteCampaign(campaign);
  }

  return (
    <>
      <div className="campaign draft" onClick={onClickCampaign}>
        <Subject campaign={campaign}/>
        <CreatedBy user={campaign.user}/>
        <div className="actions">
          <span className="link danger" onClick={onClickDiscard}>Discard</span>
        </div>
        <ChevronRight className="right-arrow"/>
      </div>
      <ConfirmModal
        isOpen={discardModalIsOpen}
        closeModal={hideDiscardModal}
        onConfirm={onConfirmDelete}
        title="Discard Draft"
        description="We’ll discard this draft campaign. This action cannot be undone. Are you sure?"
        buttonLabel="Discard Draft"
        buttonClassName="danger"
      />
    </>
  );
}


function ScheduledCampaign({ campaign }) {
  const { businessID }         = usePageContext();
  const { navigateTo }         = usePageContext();
  const convertCampaignToDraft = Campaigns.useConvertCampaignToDraft(businessID);
  const href                   = `/${businessID}/campaigns/${campaign.id}`;

  function onClickCampaign() {
    navigateTo(href);
  }

  const { scheduledAt } = campaign;
  const timeInMinutes   = useScheduledTimeInMinutes(scheduledAt);
  const canSaveForLater = timeInMinutes >= 1;

  const [ laterModalIsOpen, showLaterModal, hideLaterModal ] = useBoolean();

  function onClickSaveForLater(event) {
    event.stopPropagation();
    showLaterModal();
  }

  async function onConfirmSaveForLater() {
    hideLaterModal();
    await convertCampaignToDraft(campaign);
  }

  useEffect(function closeModalAfterTimeExpires() {
    if (laterModalIsOpen && !canSaveForLater)
      hideLaterModal();
  }, [ laterModalIsOpen, hideLaterModal, canSaveForLater ]);

  return (
    <>
      <div className="campaign scheduled" onClick={onClickCampaign}>
        <Subject campaign={campaign}/>
        <CreatedBy user={campaign.user}/>
        <div className="description">
          {canSaveForLater && <>Sending in {timeInMinutes} {pluralize(timeInMinutes, 'minute')}…</>}
          {!canSaveForLater && <>Sending now…</>}
        </div>
        {canSaveForLater && (
          <div className="actions">
            <span className="link danger" onClick={onClickSaveForLater}>Save For Later</span>
          </div>
        )}
        <ChevronRight className="right-arrow"/>
      </div>
      <ConfirmModal
        isOpen={laterModalIsOpen}
        closeModal={hideLaterModal}
        onConfirm={onConfirmSaveForLater}
        title="Save For Later"
        description="We’ll cancel sending this campaign and save it as a draft so you can send it later."
        buttonLabel="Save For Later"
        buttonClassName="danger"
      />
    </>
  );
}


export function useScheduledTimeInMinutes(scheduledAt) {
  const initialTimeInMinutes                = getMinutesFromNow(scheduledAt);
  const [ timeInMinutes, setTimeInMinutes ] = useState(initialTimeInMinutes);

  useEffect(function updateTime() {
    const timer = setInterval(() => {
      setTimeInMinutes(getMinutesFromNow(scheduledAt));
    }, ms('5s'));

    return () => {
      clearInterval(timer);
    };
  }, [ scheduledAt ]);

  return timeInMinutes;
}

function SentCampaign({ campaign }) {
  const { scheduledAt } = campaign;
  const { sentAt }      = campaign;
  const { businessID }  = usePageContext();
  const { navigateTo }  = usePageContext();
  const href            = `/${businessID}/campaigns/${campaign.id}`;

  function onClickCampaign() {
    navigateTo(href);
  }

  return (
    <div className="campaign sent" onClick={onClickCampaign}>
      <Subject campaign={campaign}/>
      <CreatedBy user={campaign.user}/>
      <p className="description">
        <CalendarTime time={sentAt || scheduledAt} long={true}/>
      </p>
      <ChevronRight className="right-arrow"/>
    </div>
  );
}


function Subject({ campaign }) {
  const { subject } = campaign;
  return (
    <h3 className="subject">{subject?.trim()}</h3>
  );
}


function CreatedBy({ user }) {
  const name = user?.name;
  if (name) {
    return (
      <div className="created-by">
        <UserIcon className="user-icon"/>
        {name}
      </div>
    );
  } else
    return null;
}


function EmptyView() {
  return (
    <div className="section empty">
      You haven’t created any campaigns yet.
    </div>
  );
}


function isDraft({ status }) {
  return status === 'draft';
}

function isScheduled({ status, scheduledAt }) {
  const minutes = getMinutesFromNow(scheduledAt);
  return status === 'scheduled' && minutes > 0;
}

function isSent({ status, scheduledAt }) {
  if (status === 'sent' || status === 'sending')
    return true;
  // treat scheduled campaigns with past-due timestamps as 'sent'
  else if (status === 'scheduled' && !isScheduled({ status, scheduledAt }))
    return true;
  else
    return false;
}

export function getMinutesFromNow(scheduledAt) {
  if (!scheduledAt)
    return 0;

  const scheduledAtDate = parseISO(scheduledAt);

  const diff = scheduledAtDate.getTime() - Date.now();
  if (diff < 0)
    return 0;
  else
    return Math.round(diff / ms('1m'));
}
